/**
 * RevenueChannelBars — a horizontal bar chart of net revenue by booking channel
 * (Recharts), for the Accounts Overview. Each bar is one channel; bars are
 * sorted highest→lowest by the caller. A tooltip reveals the exact amount and
 * the channel's share of total channel revenue.
 *
 * Money arrives as integer-minor STRINGS; we convert to major units (÷100) only
 * for plotting/formatting. Client-only (imported by the console bundle, never
 * SSR) so Recharts never runs during `renderToString`.
 */
import type { ReactElement } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
} from "recharts";

/** Brand-derived series palette (emerald family + one gold accent). */
const SERIES_COLORS = [
  "#1D35A9", // Primary blue (the strongest channel gets the accent)
  "#0067B1", // Deep blue
  "#12B76A", // Green
  "#667085", // Slate gray
  "#84CAFF", // Light blue
  "#F79009", // Amber
];
const RULE = "#EAECF0";
const SLATE = "#667085";

/** One channel row for the chart (net revenue in minor-unit STRING form). */
export interface ChannelBarRow {
  readonly channel: string;
  readonly netMinor: string;
}

interface BarDatum {
  readonly name: string;
  readonly value: number;
  readonly netMinor: string;
  readonly share: number;
}

function formatFull(minor: string): string {
  const major = Number(minor) / 100;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major.toFixed(0)}`;
  }
}

function formatCompact(major: number): string {
  if (major >= 10_000_000) return `₹${(major / 10_000_000).toFixed(1)}Cr`;
  if (major >= 100_000) return `₹${(major / 100_000).toFixed(1)}L`;
  if (major >= 1_000) return `₹${(major / 1_000).toFixed(0)}k`;
  return `₹${major.toFixed(0)}`;
}

function ChannelTooltip({
  active,
  payload,
}: {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload?: BarDatum }[];
}): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const d = payload[0]?.payload;
  if (d === undefined) {
    return null;
  }
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{d.name}</p>
      <p className="chart-tooltip__row">
        {formatFull(d.netMinor)} · {(d.share * 100).toFixed(0)}%
      </p>
    </div>
  );
}

/**
 * The channel revenue horizontal bar chart. `rows` should already be sorted
 * highest→lowest; the first bar takes the gold accent. Renders an accessible
 * empty state when there is no channel revenue.
 */
export function RevenueChannelBars({
  rows,
}: {
  readonly rows: readonly ChannelBarRow[];
}): ReactElement {
  const total = rows.reduce((sum, r) => sum + Number(r.netMinor), 0);
  const data: BarDatum[] = rows.map((r) => ({
    name: r.channel || "Direct",
    value: Number(r.netMinor) / 100,
    netMinor: r.netMinor,
    share: total > 0 ? Number(r.netMinor) / total : 0,
  }));

  if (data.length === 0 || total <= 0) {
    return (
      <p className="console-empty">
        No channel revenue yet — it will appear here when bookings are recorded.
      </p>
    );
  }

  // Height grows with the number of channels so bars stay legible.
  const height = Math.max(160, data.length * 44 + 24);

  return (
    <div className="chart-frame" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
          barCategoryGap={12}
        >
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: SLATE, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: "#101828", fontSize: 13 }}
            axisLine={{ stroke: RULE }}
            tickLine={false}
            width={110}
          />
          <Tooltip content={<ChannelTooltip />} cursor={{ fill: "rgba(20,64,47,0.04)" }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={26}>
            {data.map((entry, i) => (
              <Cell
                key={entry.name}
                fill={SERIES_COLORS[i % SERIES_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default RevenueChannelBars;
